import { useCallback, useMemo } from 'react'
import { SearchItemLabel, SearchItemLabelPropsBase, usePopup } from '.'
import { CheckboxSelectConditionProps } from '../search-components/CheckboxSelectCondition'
import { Avatar, Box, Popover, Typography, styled } from '@mui/material'
import { Checkbox } from '../../inputs/Checkbox'
import { colorPalette } from '../../../style/colorPalette'

const OptionAvatar = styled(Avatar)({
  width: '20px',
  height: '20px',
})

type CheckboxSelectConditionItemLabelProps =
  SearchItemLabelPropsBase<CheckboxSelectConditionProps> & {
    isLoading?: boolean
    onClose?: () => void
  }

export const CheckboxSelectConditionItemLabel = ({
  header,
  value,
  isLoading,
  onChange,
  onClose,
  options,
  search,
}: CheckboxSelectConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()

  const selectedOptions = useMemo(() => {
    return options.filter(op => (value || []).includes(op.value))
  }, [value, options])

  const valueNames = useMemo(() => {
    return selectedOptions.map(op => op.name)
  }, [selectedOptions])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose && onClose()
  }, [])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const onToggleValue = useCallback(
    (optionValue: string) => {
      const newValue = [...(value || [])]
      if (newValue.includes(optionValue)) {
        newValue.splice((value || []).indexOf(optionValue), 1)
      } else {
        newValue.push(optionValue)
      }
      onChange(newValue)
      if (newValue.length === 0) {
        search()
        closePopup()
        if (onClose) onClose()
      }
    },
    [value, search, closePopup, onClose]
  )

  if (selectedOptions.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {header}: {valueNames.join(',')}
      </SearchItemLabel>
      <Popover
        open={open && !isLoading}
        anchorEl={ref.current || null}
        onClose={onClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ padding: '8px' }}>
          {options.map(option => (
            <Box
              key={option.value}
              onClick={() => onToggleValue(option.value)}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: colorPalette.monotone[1],
                },
              }}
            >
              <Checkbox
                sx={{ marginRight: '8px' }}
                checked={(value || []).some(v => v === option.value)}
              />
              {option.iconUrl && <OptionAvatar src={option.iconUrl} />}
              <Typography
                sx={{
                  margin: 'auto 10px',
                  color: colorPalette.monotone[4],
                }}
              >
                {option.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  )
}
